import { request } from '@umijs/max';
import { getNebulithInferenceProApiUrl } from '@/services/util';
import type { EntitlementOperatorUser } from './entitlement';

/** 额度池模型列表 `modelTypeCodes` 入参 */
export enum ModelType {
  ASR = 'ASR',
  LLM = 'LLM',
  Embedding = 'Embedding',
  Rerank = 'Rerank',
  TTS = 'TTS',
  TextToImage = 'TextToImage',
  Tools = 'Tools',
  Vision = 'VLM',
}

export interface CreditPoolModelType {
  description?: string;
  modelTypeId?: string;
  sortOrder?: number;
  typeCode?: string;
  typeName?: string;
}

export interface CreditPoolModelItem {
  contextWindow?: number;
  description?: string;
  dimension?: number;
  gatewayModelUuid?: string;
  inferenceModelId: string;
  modelName: string;
  modelType?: CreditPoolModelType;
  modelTypeId?: string;
  status?: string;
  supportedEndpointPaths?: string[];
}

export interface CreditPoolMeterType {
  description?: string;
  measureWord?: string;
  meterCode?: string;
  meterName?: string;
  meterTypeId?: string;
  sortOrder?: number;
  status?: string;
  usageParserType?: string;
}

export interface CreditPoolBillingRule {
  billingRuleId: string;
  businessQuantity?: number;
  createAt?: string;
  createBy?: string;
  createUser?: EntitlementOperatorUser | null;
  creditAmount?: string;
  creditAmountMicro?: number;
  description?: string;
  inferenceModel?: CreditPoolModelItem;
  inferenceModelId?: string;
  meterType?: CreditPoolMeterType;
  meterTypeId?: string;
  modelType?: CreditPoolModelType;
  modelTypeId?: string;
  updateAt?: string;
  updateBy?: string;
  updateUser?: EntitlementOperatorUser | null;
}

export type CreditPoolUsageType = 'fixed' | 'metered' | 'windowLimit';

export type CreditPoolUsageStatus =
  | 'active'
  | 'exhausted'
  | 'inactive'
  | 'pending_effective';

export interface CreditPoolFixedUsage {
  maxCredit?: string;
  maxCreditMicro?: number;
  remainingCredit?: string;
  remainingCreditMicro?: number;
  usedCredit?: string;
  usedCreditMicro?: number;
  usedRatio?: number;
}

export interface CreditPoolMeteredUsage {
  usedCredit?: string;
  usedCreditMicro?: number;
}

export interface CreditPoolWindowLimitItem {
  currentLimit?: string;
  currentLimitMicro?: number;
  currentLimitRatio?: number;
  currentUsed?: string;
  currentUsedMicro?: number;
  quotaAmount?: string;
  quotaAmountMicro?: number;
  resetPeriodUnit?: string;
  resetPeriodValue?: number;
  /** 距下次重置的时长，后端原样返回展示文本 */
  resetTimeMills?: number | string;
  /** 重置周期展示文本，如 5h、7d */
  resetTimeText?: string;
  sortOrder?: number;
  usedCredit?: string;
}

export interface CreditPoolWindowLimitUsage {
  usedCredit?: string;
  usedCreditMicro?: number;
  windows?: CreditPoolWindowLimitItem[];
}

export interface CreditPoolPlanUsage {
  fixed?: CreditPoolFixedUsage;
  metered?: CreditPoolMeteredUsage;
  status?: CreditPoolUsageStatus | string;
  type?: CreditPoolUsageType | string;
  windowLimit?: CreditPoolWindowLimitUsage;
}

export interface CreditPoolPlanInfo {
  creditAmount?: string;
  creditAmountMicro?: number;
  description?: string;
  planId?: string;
  planName?: string;
  planType?: string;
  priceAmount?: number;
  priceAmountYuan?: string;
  status?: string;
  trialValidityDays?: number;
}

export type CreditPoolPlanInstanceStatus =
  | 'pending_payment'
  | 'using'
  | 'cancelled'
  | 'expired';

export interface CreditPoolPlanInstance {
  billingTenantId?: string;
  effectiveEndAt?: string;
  effectiveStartAt?: string;
  instanceId?: string;
  nextRenewAt?: string;
  plan?: CreditPoolPlanInfo;
  planId?: string;
  planInstanceId: string;
  planInstanceNo?: string;
  planType?: string;
  purchasePriceAmount?: number;
  purchasePriceAmountYuan?: string;
  status?: CreditPoolPlanInstanceStatus | string;
  usage?: CreditPoolPlanUsage;
  userId?: string;
}

export interface CreditPoolModelDetailResult {
  billingRules?: CreditPoolBillingRule[];
  model?: CreditPoolModelItem;
  planInstances?: CreditPoolPlanInstance[];
}

export interface CreditPoolModelsParams {
  userId?: string;
  modelName?: string;
  modelTypeCodes?: string[];
}

export interface CreditPoolModelsResult {
  list: CreditPoolModelItem[];
}

export interface CreditPoolModelParams {
  inferenceModelId: string;
  userId?: string;
}

/** 用户额度池可调用的模型服务列表（无分页） */
export async function getCreditPoolModels(
  params?: CreditPoolModelsParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<CreditPoolModelsResult>(
    getNebulithInferenceProApiUrl('credit/pool/models'),
    {
      method: 'POST',
      data: {
        ...(params || {}),
        hasEffectivePlanAccess: true,
      },
      ...(options || {}),
    },
  );
}

/** 用户额度池可调用的单个模型详情 */
export async function getCreditPoolModel(
  params: CreditPoolModelParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<CreditPoolModelDetailResult>(
    getNebulithInferenceProApiUrl('credit/pool/model'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}
